import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { fetchMembership, MembershipData } from "@/services/membershipService";
import { getUrlParam } from "@/utils/urlUtils";
import i18next from "i18next";
import {
  membershipFirstBaseUrl,
  membershipFirstRedirectUrl,
} from "@/utils/membershipFirstRedirect";
import type { Product } from "@/components/product-step/types";

interface MembershipContextValue {
  /** The full membership data, or null if no membership */
  membership: MembershipData | null;
  /** True when membership data was successfully fetched */
  isMember: boolean;
  /** True while the membership API call is in flight */
  isLoading: boolean;
  /**
   * Server-resolved current usable entitlement type (baseline before followup).
   * Null if none available or not a member.
   */
  currentEntitlementType: string | null;
  /** `?showAllBloodTests=true` in the URL — unlocks the follow-up blood test regardless of state. */
  showAllBloodTestsFlag: boolean;
  /** Counter for follow-up check-in badge. Null when baseline or non-member. */
  followUpCounter: { current: number; total: number; remaining: number } | null;
  /**
   * Check if a product (by stripe_product_id) is entitled (included at CHF 0).
   */
  isEntitledProduct: (stripeProductId: string | undefined) => boolean;
  /**
   * Get the effective price for a product in cents:
   * - Entitled product → 0
   * - Has Stripe member_price → member_price.unit_amount
   * - Fallback (no member_price set) → % discount from membership token
   * Always returns a whole-CHF value (floored).
   */
  getEffectivePrice: (product: Product) => number;
}

const MembershipContext = createContext<MembershipContextValue>({
  membership: null,
  isMember: false,
  isLoading: false,
  currentEntitlementType: null,
  showAllBloodTestsFlag: false,
  followUpCounter: null,
  isEntitledProduct: () => false,
  getEffectivePrice: (product) => product.default_price?.unit_amount ?? 0,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useMembership = () => useContext(MembershipContext);

export const MembershipProvider: React.FC<{
  children: React.ReactNode;
  /** Forward a membership bought on the membership-first flow to the start
   *  app, where it is booked. Off in the membership-first app itself. */
  redirectMembershipFirst?: boolean;
}> = ({ children, redirectMembershipFirst = true }) => {
  const [membership, setMembership] = useState<MembershipData | null>(null);
  const [isLoading, setIsLoading] = useState(
    Boolean(getUrlParam("membershipToken")),
  );

  const token = getUrlParam("membershipToken");
  // Optional per-environment override of the membership API origin, forwarded
  // by the linking app (the report app passes `membershipDomain` set to its own
  // environment's membership-app URL) so the token resolves against the
  // database that created the membership. Mirrors the `reportBackendDomain`
  // override used for the activation backend.
  const membershipDomain = getUrlParam("membershipDomain");
  const isMember = membership !== null;
  const showAllBloodTestsFlag = getUrlParam("showAllBloodTests") === "true";
  const currentEntitlementType =
    membership?.currentAvailableEntitlement?.entitlement_type ?? null;

  const followUpCounter = React.useMemo(() => {
    if (!membership) return null;
    const entitlements = membership.membership_entitlements || [];
    const followUps = entitlements.filter(
      (e) => e.entitlement_type === "followup_blood_test",
    );
    if (followUps.length === 0) return null;
    const used = followUps.filter(
      (e) => e.status === "booked" || e.status === "completed",
    ).length;
    const current = used + 1;
    const total = followUps.length;
    const remaining = Math.max(0, total - current);
    return { current, total, remaining };
  }, [membership]);

  useEffect(() => {
    if (!token) return;

    setIsLoading(true);
    fetchMembership(token, membershipDomain)
      .then((data) => {
        const forward =
          data && redirectMembershipFirst
            ? membershipFirstRedirectUrl(
                data,
                { token, locale: i18next.language ?? null, membershipDomain },
                membershipFirstBaseUrl(
                  import.meta.env.VITE_MEMBERSHIP_FIRST_URL,
                  window.location.hostname,
                ),
              )
            : null;
        if (forward) {
          window.location.replace(forward);
          return;
        }
        setMembership(data);

        if (data) {
          prefillFormData(data);
        }
      })
      .catch((err) => {
        console.error("[MembershipProvider] Failed to fetch membership:", err);
      })
      .finally(() => setIsLoading(false));
  }, [token, membershipDomain, redirectMembershipFirst]);

  const isEntitledProduct = useCallback(
    (stripeProductId: string | undefined): boolean => {
      if (!membership || !stripeProductId) return false;
      return membership.entitledProductIds.includes(stripeProductId);
    },
    [membership],
  );

  const getEffectivePrice = useCallback(
    (product: Product): number => {
      if (!membership) return product.default_price?.unit_amount ?? 0;

      // Entitled product (e.g. blood test included in plan) → free
      if (isEntitledProduct(product.stripe_product_id)) return 0;

      // Authoritative Stripe member price → use it directly
      if (product.member_price?.unit_amount !== undefined) {
        return product.member_price.unit_amount;
      }

      // Fallback: apply % or fixed discount from membership token
      // (covers products that don't yet have a Stripe member price)
      const original = product.default_price?.unit_amount ?? 0;
      let result = original;
      if (membership.membershipDiscountPercentage) {
        result = original * (1 - membership.membershipDiscountPercentage / 100);
      } else if (membership.membershipDiscountFixValue) {
        result = Math.max(0, original - membership.membershipDiscountFixValue);
      }
      return Math.floor(result / 100) * 100;
    },
    [membership, isEntitledProduct],
  );

  return (
    <MembershipContext.Provider
      value={{
        membership,
        isMember,
        isLoading,
        currentEntitlementType,
        showAllBloodTestsFlag,
        followUpCounter,
        isEntitledProduct,
        getEffectivePrice,
      }}
    >
      {children}
    </MembershipContext.Provider>
  );
};

/**
 * Pre-fill the booking form data in localStorage from membership info.
 * Merges with existing data — does NOT overwrite fields the user already filled.
 * Also sets userEmail to bypass the email capture step.
 */
function prefillFormData(data: MembershipData) {
  try {
    const existingRaw = localStorage.getItem("bookingFormData");
    const existing = existingRaw ? JSON.parse(existingRaw) : {};

    const merged = {
      ...existing,
      // Membership data takes priority — it identifies the actual member
      firstName: data.first_name || existing.firstName || "",
      lastName: data.last_name || existing.lastName || "",
      email: data.email || existing.email || "",
    };

    localStorage.setItem("bookingFormData", JSON.stringify(merged));

    // Bypass email capture step — membership email takes priority
    if (data.email) {
      localStorage.setItem("userEmail", data.email);
    }
  } catch (err) {
    console.error("[MembershipProvider] Failed to pre-fill form data:", err);
  }
}
